import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "@/lib/apiClient";
import { LIST_PARAMS, useSearchParamsWriter } from "@/hooks/useUrlParam";
import { useRememberListUrl } from "@/hooks/useBackTo";
import { useRestoreListScroll } from "@/hooks/useListScroll";
import {
  DEFAULT_PAGE_SIZE,
  emptyPage,
  type Paged,
  type PageParams,
  type SortClause,
} from "@/lib/paging";

type SortDir = "asc" | "desc";

interface Options {
  pageSize?: number;
  initialStatus?: string;
  initialKeyword?: string;
  initialSortBy?: string;
  initialSortDir?: SortDir;
  /**
   * Multi-column order applied while no column header is active. Picking a column replaces it
   * entirely, so the header stays the single source of truth for what the user sees.
   */
  defaultSort?: SortClause[];
  /** Extra server params (e.g. { type: "order" }, { state: "open" }); changes re-fetch + reset to page 1. */
  extra?: Record<string, string | undefined>;
  syncUrl?: boolean;
}

interface ListQuery {
  pageNumber: number;
  keyword: string;
  status: string;
  sortBy: string;
  sortDir: SortDir;
}

function readListQuery(params: URLSearchParams, defaults: ListQuery): ListQuery {
  const page = Number(params.get(LIST_PARAMS.page));
  const dir = params.get(LIST_PARAMS.sortDir);
  return {
    pageNumber: Number.isInteger(page) && page > 0 ? page : defaults.pageNumber,
    keyword: params.get(LIST_PARAMS.keyword) ?? defaults.keyword,
    status: params.get(LIST_PARAMS.status) ?? defaults.status,
    sortBy: params.get(LIST_PARAMS.sortBy) ?? defaults.sortBy,
    sortDir: dir === "asc" || dir === "desc" ? dir : defaults.sortDir,
  };
}

function writeListQuery(params: URLSearchParams, query: ListQuery, defaults: ListQuery) {
  const put = (key: string, value: string, fallback: string) => {
    if (value === fallback) params.delete(key);
    else params.set(key, value);
  };
  put(LIST_PARAMS.page, String(query.pageNumber), String(defaults.pageNumber));
  put(LIST_PARAMS.keyword, query.keyword, defaults.keyword);
  put(LIST_PARAMS.status, query.status, defaults.status);
  put(LIST_PARAMS.sortBy, query.sortBy, defaults.sortBy);
  put(LIST_PARAMS.sortDir, query.sortDir, defaults.sortDir);
}

type Fetcher<T> = (params: PageParams & Record<string, unknown>) => Promise<Paged<T>>;

interface PagedListState<T> {
  data: Paged<T>;
  loading: boolean;
  loaded: boolean;
  error: string | null;
  forbidden: boolean;
  pageNumber: number;
  keyword: string;
  status: string;
  sortBy: string;
  sortDir: SortDir;
  setPage: (pageNumber: number) => void;
  setKeyword: (keyword: string) => void;
  setStatus: (status: string) => void;
  setSort: (sortBy: string, sortDir: SortDir) => void;
  reload: () => void;
}

export function usePagedList<T>(fetcher: Fetcher<T>, opts: Options = {}): PagedListState<T> {
  const pageSize = opts.pageSize ?? DEFAULT_PAGE_SIZE;

  const [data, setData] = useState<Paged<T>>(() => emptyPage<T>(pageSize));
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const [defaults] = useState<ListQuery>(() => ({
    pageNumber: 1,
    keyword: opts.initialKeyword ?? "",
    status: opts.initialStatus ?? "",
    sortBy: opts.initialSortBy ?? "",
    sortDir: opts.initialSortDir ?? "asc",
  }));
  const syncUrl = opts.syncUrl === true;
  useRememberListUrl(syncUrl);
  const [local, setLocal] = useState<ListQuery>(defaults);
  const [searchParams, writeParams] = useSearchParamsWriter();
  const fromUrl = readListQuery(searchParams, defaults);
  const { pageNumber, keyword, status, sortBy, sortDir } = syncUrl ? fromUrl : local;

  const update = useCallback(
    (patch: Partial<ListQuery>) => {
      if (!syncUrl) {
        setLocal((q) => ({ ...q, ...patch }));
        return;
      }
      writeParams((p) => writeListQuery(p, { ...readListQuery(p, defaults), ...patch }, defaults));
    },
    [syncUrl, writeParams, defaults],
  );
  const [reloadKey, setReloadKey] = useState(0);
  useRestoreListScroll(loaded, syncUrl);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const extraKey = JSON.stringify(opts.extra ?? {});
  const extraRef = useRef(opts.extra);
  extraRef.current = opts.extra;

  const defaultSortKey = JSON.stringify(opts.defaultSort ?? []);
  const defaultSortRef = useRef(opts.defaultSort);
  defaultSortRef.current = opts.defaultSort;

  const lastExtraKey = useRef(extraKey);
  useEffect(() => {
    if (lastExtraKey.current === extraKey) return;
    lastExtraKey.current = extraKey;
    update({ pageNumber: 1 });
  }, [extraKey, update]);

  const sort = useMemo<SortClause[]>(() => {
    if (sortBy) return [{ by: sortBy, dir: sortDir }];
    return defaultSortRef.current ?? [];
  }, [sortBy, sortDir, defaultSortKey]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setForbidden(false);

    fetcherRef
      .current({
        pageNumber,
        pageSize,
        keyword,
        status,
        sortBy: sortBy || undefined,
        sortDir: sortBy ? sortDir : undefined,
        sort: sort.length > 0 ? sort : undefined,
        ...(extraRef.current ?? {}),
      })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 403) setForbidden(true);
        else setError(e instanceof Error ? e.message : "Failed to load.");
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [pageNumber, pageSize, keyword, status, sortBy, sortDir, sort, extraKey, reloadKey]);

  const setPage = useCallback((p: number) => update({ pageNumber: Math.max(1, p) }), [update]);
  const setKeyword = useCallback((k: string) => update({ keyword: k, pageNumber: 1 }), [update]);
  const setStatus = useCallback((st: string) => update({ status: st, pageNumber: 1 }), [update]);
  const setSort = useCallback(
    (by: string, dir: SortDir) => update({ sortBy: by, sortDir: dir, pageNumber: 1 }),
    [update],
  );
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  return {
    data,
    loading,
    loaded,
    error,
    forbidden,
    pageNumber,
    keyword,
    status,
    sortBy,
    sortDir,
    setPage,
    setKeyword,
    setStatus,
    setSort,
    reload,
  };
}
